import { z } from 'zod';
import { catalogPartStatusSchema, hardwareCategorySchema } from '../enums.js';
import { CATALOG_IMAGE_CONTENT_TYPES } from '../constants.js';
/**
 * Catalogue de pièces (ADR-0030) : le référentiel **global curé**. Une entrée = une référence
 * officielle **unique** (identité canonique `category + brand + model + variant`). Le contrat décrit
 * de la **donnée métier**, sans notion de présentation (discipline ADR-0029 §5).
 */
/** Identifiant canonique en `slug` : stable et lisible, il sert de clé de référence inter-organisations. */
export const catalogPartIdSchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug attendu (a-z, 0-9, tirets)')
    .max(80);
/**
 * Specs d'une pièce : un dictionnaire plat **clé → scalaire**, volontairement **permissif** pour
 * rester évolutif — ajouter un champ (ou une catégorie) n'impose aucune migration ni changement de
 * contrat. Le référentiel des champs *attendus* par catégorie vit dans la donnée de seed
 * (`_meta.specFieldsByCategory`) et l'UI ; une validation par **union discriminée par catégorie**
 * (ADR-0030 §2) pourra se superposer plus tard sans rupture, la donnée restant compatible.
 */
export const catalogSpecsSchema = z.record(z.string().min(1).max(60), z.union([z.string().max(200), z.number(), z.boolean()]));
const shortText = z.string().trim().max(120);
/** Corps de création / mise à jour d'une référence officielle (écriture réservée au `SUPERADMIN`). */
export const upsertCatalogPartSchema = z.object({
    id: catalogPartIdSchema,
    category: hardwareCategorySchema,
    brand: shortText.min(1),
    model: shortText.min(1),
    // `variant` distingue deux produits d'un même modèle (KV, taille…). Vide = modèle sans déclinaison.
    variant: shortText.default(''),
    specs: catalogSpecsSchema.default({}),
    // Liens fabricant / revendeur qui **sourcent** la référence (traçabilité de la curation).
    sources: z.array(z.string().url().max(500)).max(20).default([]),
    status: catalogPartStatusSchema.default('ACTIVE'),
    // Point d'accroche média (photo produit), servi plus tard par URL pré-signée (ADR-0010). Optionnel,
    // additif : l'ajout d'images ne rouvre pas le contrat.
    mediaKey: shortText.optional(),
});
/** Référence du catalogue telle que restituée par l'API. */
export const catalogPartSchema = upsertCatalogPartSchema.extend({
    variant: z.string(),
    sources: z.array(z.string()),
    mediaKey: z.string().nullable(),
    // URL de lecture pré-signée de l'illustration (ADR-0032), `null` si aucune. Dérivée à la lecture,
    // jamais stockée : le `mediaKey` est la donnée, l'URL n'en est qu'une projection éphémère.
    mediaUrl: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Illustration d'une pièce (ADR-0032) — curation `SUPERADMIN`. Deux voies : **import depuis l'URL
 * fabricant** (le serveur télécharge une fois, avec liste blanche d'hôtes + anti-SSRF) ou **upload**
 * de fichier (dépôt pré-signé). L'URL import est validée `http(s)` ; l'hôte est revérifié côté serveur.
 */
export const importCatalogMediaSchema = z.object({
    sourceUrl: z.string().url().max(1000),
});
export const reserveCatalogMediaSchema = z.object({
    contentType: z.enum(CATALOG_IMAGE_CONTENT_TYPES),
});
/** Réponse d'ouverture d'un upload d'illustration : URL de dépôt pré-signée, valable un temps borné. */
export const catalogMediaUploadSchema = z.object({
    uploadUrl: z.string().url(),
    expiresAt: z.coerce.date(),
});
/** Résultat d'un import / d'une confirmation : l'URL de lecture de l'illustration recopiée. */
export const catalogMediaResultSchema = z.object({
    mediaUrl: z.string(),
});
/* ------------------------------------------------------------------ *
 * Variante d'organisation (ADR-0030 §3)
 *
 * Une version « tunée » d'une pièce, **cloisonnée** et **toujours rattachée** (enfant) à une référence
 * officielle (parent). Elle ne porte que des **surcharges** de specs ; elle hérite du parent, jamais
 * une copie divergente. Le rattachement obligatoire est porté par le schéma (parentPartId requis) et
 * par la FK côté base.
 * ------------------------------------------------------------------ */
export const upsertOrgPartVariantSchema = z.object({
    // Enfant obligatoire d'un officiel : le slug du parent au catalogue global.
    parentPartId: catalogPartIdSchema,
    label: shortText.min(1),
    specOverrides: catalogSpecsSchema.default({}),
    note: z.string().trim().max(500).optional(),
});
export const orgPartVariantSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    parentPartId: z.string(),
    label: z.string(),
    specOverrides: catalogSpecsSchema,
    note: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/* ------------------------------------------------------------------ *
 * Nomenclature (BOM) d'un matériel (ADR-0030 §4)
 *
 * Chaque ligne **nomme toujours l'officiel** (`catalogPartId` requis), plus une variante tunée
 * optionnelle (`orgVariantId`) — dont le parent **doit** être ce `catalogPartId` (invariant vérifié à
 * l'écriture). D'où un sourcing inter-organisations trivial : regrouper par `catalogPartId`.
 * ------------------------------------------------------------------ */
export const buildItemInputSchema = z.object({
    catalogPartId: catalogPartIdSchema,
    // Variante d'org employée ; son parent doit être `catalogPartId` (vérifié côté service).
    orgVariantId: z.string().nullable().default(null),
    quantity: z.number().int().min(1).max(999).default(1),
    // Emplacement / rôle dans le build (ex. « FRAME », « MOTOR »), libre.
    slot: shortText.optional(),
});
/** Corps de remplacement de la nomenclature d'un matériel (idempotent : remplace la liste entière). */
export const replaceBuildSchema = z.object({
    items: z.array(buildItemInputSchema).max(100).default([]),
});
/** Ligne de nomenclature telle que restituée (références brutes ; le front résout depuis le catalogue). */
export const hardwareBuildItemSchema = z.object({
    id: z.string(),
    assetId: z.string(),
    organizationId: z.string(),
    catalogPartId: z.string(),
    orgVariantId: z.string().nullable(),
    quantity: z.number().int(),
    slot: z.string().nullable(),
    createdAt: z.coerce.date(),
});
//# sourceMappingURL=catalog.js.map